import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Logo from "@/components/Logo";
import { useApp } from "@/context/AppContext";

export default function Header() {
  const { cities, selectedCity, chooseCity } = useApp();
  const [q, setQ] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const nav = useNavigate();

  const cityLabel = useMemo(() => {
    const c = cities.find((x) => x.key === selectedCity);
    return c ? c.name : "Select City";
  }, [cities, selectedCity]);

  const filteredCities = useMemo(() => {
    const s = cityQuery.trim().toLowerCase();
    if (!s) return cities;
    return cities.filter((c) => c.name.toLowerCase().includes(s));
  }, [cities, cityQuery]);

  const submitSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    nav(`/?${params.toString()}`);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-black/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center gap-4">
        <div className="shrink-0"><Logo size="md" /></div>

        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="city-dropdown-trigger"
              className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 md:px-4 h-10 hover:border-[#FA003F] transition-colors"
            >
              <MapPin className="h-4 w-4 text-[#FA003F]" />
              <span className="text-sm font-semibold max-w-[110px] truncate">{cityLabel}</span>
              <ChevronDown className="h-4 w-4 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0 rounded-2xl overflow-hidden">
            <div className="p-3 border-b border-black/5">
              <Input
                data-testid="city-search-input"
                placeholder="Search your city…"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                className="rounded-full"
              />
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {filteredCities.length === 0 && (
                <div className="px-4 py-6 text-sm text-gray-500">No cities available</div>
              )}
              {filteredCities.map((c) => (
                <button
                  key={c.key}
                  data-testid={`city-option-${c.key}`}
                  onClick={() => {
                    chooseCity(c.key);
                    setCityOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#FA003F]/10 flex items-center justify-between ${
                    selectedCity === c.key ? "text-[#FA003F] font-bold" : "text-[#1A1A1A]"
                  }`}
                >
                  <span>{c.name}</span>
                  <span className="text-xs text-gray-400">{c.state}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <form onSubmit={submitSearch} className="flex-1 max-w-xl hidden md:block">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, venues, artists…"
              className="pl-11 h-11 rounded-full bg-white border-black/10"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2">
          <Button
            data-testid="header-list-event-btn"
            onClick={() => nav("/list-event")}
            className="rounded-full bg-[#1A1A1A] hover:bg-[#FA003F] transition-colors h-10 px-5"
          >
            List Your Event
          </Button>
        </div>
      </div>

      <div className="md:hidden px-4 pb-3">
        <form onSubmit={submitSearch}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              data-testid="header-search-input-mobile"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events…"
              className="pl-11 h-11 rounded-full bg-white"
            />
          </div>
        </form>
      </div>
    </header>
  );
}
